import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fundingAuthAPI, resolveIpeLoginErrorMessage } from '@/utils/api';
import { showToast } from '@/utils/toast';

import {
  DEFAULT_COOLDOWN_SECONDS,
  getSmsSendCooldownRemaining,
  setSmsSendCooldown,
} from '@/utils/smsSendCooldown';

const phonePattern = /^1[3-9]\d{9}$/;
const SEND_STATUS_SYNC_MS = 20_000;

function resolveRetryAfterSeconds(source, fallback = DEFAULT_COOLDOWN_SECONDS) {
  const value = source?.retryAfterSeconds ?? source?.payload?.retryAfterSeconds;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function applySendStatusToCountdown(data, phone, scene, setCountdown) {
  if (!data || data.canSend) {
    setCountdown(0);
    return;
  }
  const retryAfter = Number(data.retryAfterSeconds);
  if (!Number.isFinite(retryAfter) || retryAfter <= 0) {
    setCountdown(0);
    return;
  }
  setCountdown(retryAfter);
  if (retryAfter <= DEFAULT_COOLDOWN_SECONDS) {
    setSmsSendCooldown(phone, scene, retryAfter);
  }
}

export default function IpeSmsCodeField({
  phone,
  scene,
  value,
  onChange,
  onSendCode,
  disabled = false,
  placeholder,
}) {
  const { t } = useTranslation('login');
  const codePlaceholder = placeholder || t('auth.codePlaceholder');
  const [countdown, setCountdown] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const statusSyncRef = useRef(null);

  const fetchSendStatus = useCallback(async () => {
    const p = String(phone || '').trim();
    if (!phonePattern.test(p)) {
      setCountdown(0);
      return;
    }
    try {
      const data = await fundingAuthAPI.getSmsSendStatus(p, scene);
      applySendStatusToCountdown(data, p, scene, setCountdown);
    } catch {
      // 查询失败时保留当前倒计时，避免闪烁
    }
  }, [phone, scene]);

  useEffect(() => {
    const p = String(phone || '').trim();
    if (!phonePattern.test(p)) {
      setCountdown(0);
      return;
    }
    setCountdown(getSmsSendCooldownRemaining(p, scene));
    fetchSendStatus();
  }, [phone, scene, fetchSendStatus]);

  useEffect(() => {
    if (countdown <= 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (countdown <= 0) {
      if (statusSyncRef.current) {
        window.clearInterval(statusSyncRef.current);
        statusSyncRef.current = null;
      }
      return undefined;
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchSendStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    statusSyncRef.current = window.setInterval(fetchSendStatus, SEND_STATUS_SYNC_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (statusSyncRef.current) {
        window.clearInterval(statusSyncRef.current);
        statusSyncRef.current = null;
      }
    };
  }, [countdown, fetchSendStatus]);

  const handleSend = useCallback(async () => {
    setSendError('');
    const p = String(phone || '').trim();
    if (!phonePattern.test(p)) {
      setSendError(t('auth.codeNeedPhone'));
      return;
    }

    const localRemaining = getSmsSendCooldownRemaining(p, scene);
    if (localRemaining > 0) {
      setCountdown(localRemaining);
      setSendError(t('auth.codeTooSoon', { seconds: localRemaining }));
      return;
    }

    setSending(true);
    try {
      const data = await onSendCode(p, scene);
      const retryAfter = resolveRetryAfterSeconds(data);
      setSmsSendCooldown(p, scene, retryAfter);
      setCountdown(retryAfter);
      showToast(t('auth.codeSent'), 'success');
    } catch (err) {
      const retryAfter = resolveRetryAfterSeconds(err, 0);
      if (retryAfter > 0) {
        setCountdown(retryAfter);
        if (retryAfter <= DEFAULT_COOLDOWN_SECONDS) {
          setSmsSendCooldown(p, scene, retryAfter);
        }
      } else {
        await fetchSendStatus();
      }
      setSendError(resolveIpeLoginErrorMessage(err, t('auth.codeSendFailed')));
    } finally {
      setSending(false);
    }
  }, [fetchSendStatus, onSendCode, phone, scene, t]);

  return (
    <div className="ipe-auth-modal__sms-field">
      <div className="ipe-auth-modal__sms-composite">
        <input
          type="text"
          className="ipe-auth-modal__sms-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder={codePlaceholder}
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
          maxLength={6}
          disabled={disabled || sending}
        />
        <button
          type="button"
          className="ipe-auth-modal__sms-btn"
          onClick={handleSend}
          disabled={disabled || sending || countdown > 0}
        >
          {countdown > 0 ? `${countdown}s` : sending ? t('auth.codeSending') : t('auth.getCode')}
        </button>
      </div>
      {sendError ? (
        <p className="ipe-auth-modal__sms-error" role="alert">
          {sendError}
        </p>
      ) : null}
    </div>
  );
}

export { phonePattern };
